// Разбивката по терминал — редът, по който реално се прави сверката.
//
// Показателите горе сумират ВСИЧКИ терминали, но „край на деня" се вади от
// всеки апарат поотделно: без този блок човек с два пинпада няма как да
// разбере кой от двата не е излязъл. Затова тук са същите три числа, но на ред
// за терминал — и редът се маха от само себе си, когато терминалът е един.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { parseDecimal } from "../../../../components/dataTable";
import type { CardTerminalSummary } from "../../../../api/reportsCardPayments";

type Props = {
  terminals: CardTerminalSummary[];
  mountLabel: (mount: string) => string;
  isNarrow: boolean;
};

/** Последните 6 знака от GUID-а стигат, за да се различат два апарата. */
const shortGuid = (guid: string | null): string =>
  guid ? guid.slice(-6).toUpperCase() : "";

export const TerminalsCard = ({ terminals, mountLabel, isNarrow }: Props) => {
  const { t } = useTranslation();

  // Един терминал = разбивката повтаря показателите отгоре и само шуми.
  if (terminals.length < 2) return null;

  return (
    <section className="flex flex-col overflow-hidden rounded-[14px] border border-shade-2 bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-shade-2 px-4 py-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
            {t("reportsPage.cardPaymentsTerminalsKicker")}
          </span>
          <span className="text-[16px] font-extrabold leading-none text-ink">
            {t("reportsPage.cardPaymentsTerminalsTitle", {
              count: terminals.length,
            })}
          </span>
        </div>
      </div>

      <ul className="divide-y divide-shade-1">
        {terminals.map((terminal) => {
          const consumed = parseDecimal(terminal.consumed_total);
          const unresolved = parseDecimal(terminal.unresolved_total);
          const voided = parseDecimal(terminal.voided_total);
          return (
            <li
              key={`${terminal.printer_guid ?? "—"}:${terminal.mount}`}
              className={`flex gap-3 px-4 py-3 ${
                isNarrow ? "flex-col" : "items-center justify-between"
              }`}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-[13px] font-bold text-ink">
                  {mountLabel(terminal.mount)}
                </span>
                {terminal.printer_guid ? (
                  <span className="shrink-0 rounded-md bg-fill px-1.5 py-0.5 font-pos-mono text-[10.5px] font-bold text-muted">
                    {shortGuid(terminal.printer_guid)}
                  </span>
                ) : null}
              </div>

              <div
                className={`grid gap-x-5 gap-y-1 ${
                  isNarrow ? "grid-cols-3" : "grid-cols-[auto_auto_auto]"
                }`}
              >
                <TerminalNumber
                  label={t("reportsPage.cardPaymentsConsumedKicker")}
                  value={fmtEuro(consumed)}
                  count={terminal.consumed_count}
                />
                <TerminalNumber
                  label={t("reportsPage.cardPaymentsUnresolvedKicker")}
                  value={fmtEuro(unresolved)}
                  count={terminal.unresolved_count}
                  hot={terminal.unresolved_count > 0}
                />
                <TerminalNumber
                  label={t("reportsPage.cardPaymentsVoidedKicker")}
                  value={fmtEuro(voided)}
                  count={terminal.voided_count}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

const TerminalNumber = ({
  label,
  value,
  count,
  hot = false,
}: {
  label: string;
  value: string;
  count: number;
  hot?: boolean;
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="font-pos-mono text-[9.5px] font-bold uppercase leading-none tracking-[0.08em] text-muted">
      {label}
    </span>
    <span
      className={`font-pos-mono text-[13.5px] font-extrabold tabular-nums leading-none ${
        hot ? "text-danger-ink" : "text-ink"
      }`}
    >
      {value}
    </span>
    <span className="font-pos-mono text-[10.5px] font-semibold tabular-nums leading-none text-muted">
      {count}
    </span>
  </div>
);
